import { Icon } from '@mui/material';
import { checkViewPath, replaceIfItemView } from '@/utils/Table';

// Set to true to also colorize submenu items with the parent's bgColor.
// Set to false to only colorize the parent row.
export const colorSubmenu = true;

export const getMenus = (parent = false) => {
    let name = 'menus';
    if(parent){
        name = 'menusoriginal';
    }
    const menus = localStorage.getItem(name);
    try {
        return menus ? JSON.parse(menus) : null;
    } catch (e) {
        return menus;
    }
}

export const saveMenus = (menus, parent = false) => {
    if (!menus) return;
    let name = 'menus';
    if(parent){
        name = 'menusoriginal';
    }
    const value = typeof menus === 'string' ? menus : JSON.stringify(menus);
    localStorage.setItem(name, value);
}

export const removeMenus = (parent = false) => {

    let name = 'menus';
    if(parent){
        name = 'menusoriginal';
    }

    localStorage.removeItem(name);
}

// https://fonts.google.com/icons?icon.set=Material+Icons
const getMenuIcon = (iconName, color = 'inherit') => {
    return iconName ? <Icon sx={{ color: color }} className="menu-custom-icon">{iconName.toLowerCase()}</Icon> : null;
};

// from backend, saved on local storage
const getMenuOptions = (t) => {

    const flatMenus = getMenus();

    if(!flatMenus || !Array.isArray(flatMenus)){
        return [];
    }

    // sort all by order first
    const sorted = [...flatMenus].sort((a,b) => (a.order || 0) - (b.order || 0));

    const buildTree = (parentId = null, parentColor = null) => {
        return sorted
            .filter(item => item.menu_id === parentId)
            .map(item => {
                const currentColor = item.color || parentColor;
                const hasChildren = sorted.some(child => child.menu_id === item.id);
                return {
                    segment: item.path,
                    title: 
                        item?.label && item.label?.length > 0 && item.label !== t(item.label) ?
                            t(item.label)
                        : 
                            t(item.name)
                    ,
                    icon: getMenuIcon(item.icon, item.color ? 'white' : currentColor),
                    bgColor: item.color,
                    //(users/manage|users/stats|users/departments|users/view/[0-9]+)
                    //(notifications/list|notifications/manage)
                    //(tickets/manage|tickets/stats|tickets/view/[0-9]+)
                    //planification_reports{/:section}
                    //planification_activities{/view/:id}
                    //`(${item.path}/[^/]+|${item.path}/[^/]+/[0-9]+|${item.path}/[^/]+/[0-9]+/[0-9]+)`
                    //`${item.path}(/.*)?`
                    pattern: hasChildren ? 
                            null 
                        : 
                            `${item.path}(/.*)?`
                    ,
                    params: item?.params,
                    children: buildTree(item.id, currentColor)
                };
            });
    };

    return buildTree(null);
}

export const getMenu = (t) => {

    const menuOptions = getMenuOptions(t);
    const menuConfig = {};

    if(!menuOptions){
        return [[], {}];
    }

    // Perform a breadth-first style traversal to build configuration and paths
    // Similar to old version's while loop but simpler since roles are pre-filtered
    const processItems = (items, parentPath = '') => {
        return items.map(processedItem => {
            const currentPath = parentPath ? `${parentPath}/${processedItem.segment}` : processedItem.segment;
            
            // Set final properties
            processedItem.path = currentPath;
            processedItem.parent = parentPath || null;

            // Register in menuConfig for easy lookup by path
            menuConfig[currentPath] = processedItem;

            // Recursively process children if any
            if (processedItem.children && processedItem.children.length > 0) {
                processedItem.children = processItems(processedItem.children, currentPath);
            } else {
                delete processedItem.children; // Cleanup empty children arrays
            }

            return processedItem;
        });
    };

    const processedMenu = processItems(menuOptions);
    
    return [processedMenu, menuConfig];
}

// check if the user has permission on the menu
const hasMenuPermission = (user,menu,menuConfig) => {

    var hasPermission = false;

    // if home
    if(menu.length === 0){
        hasPermission = true;
    }
    else{
        // if notifications (not in the menu)
        if(menu === 'notifications'){
            hasPermission = true;
        }
        else{
            // versions only root user can see
            if(menu === 'versions' && user?.roles_tags?.includes('root')){
                hasPermission = true;
            }
            else{
                // if menu allowed for the user
                if(menuConfig[menu]){
                    hasPermission = true;
                }
            }
        }
    }

    return hasPermission;
}

// to get the menu from the path
export const getMenuFromPath = (user, menuConfig, path = null) => {

    if(!menuConfig) return null;

    if(!path){
        path = window.location.pathname;
    }

    // 1. Clean and normalize the path
    // remove leading / and replace item/view/N
    let currentPath = replaceIfItemView(path).replace(/^\//, '');
    
    if (currentPath === '') return ''; // Home

    // 2. Try to find the most specific match in menuConfig by popping segments
    let segments = currentPath.split('/').filter(Boolean);

    while (segments.length > 0) {
        const potentialMenu = segments.join('/');
        
        // Check if this path (or subpath) has permission in menuConfig
        if (hasMenuPermission(user, potentialMenu, menuConfig)) {
            return potentialMenu;
        }
        
        // If not found, try the parent level
        segments.pop();
    }

    return null;
}

// set the breadcrumbs for the pages
// using this to show/hide conditionally
export const getBreadcrumb = (user, router, menuConfig) => {
    
    var path = router.pathname;
    
    /*
    // if item/view/N do not show breadcrumb
    if(checkViewPath(path)){
        return [];
    }

    const split = path.split('/').filter(Boolean);
    if(split.length < 2) return [];

    // Exclusions
    const parent = split[0];
    const excluded = [];
    if(excluded.includes(parent)) return [];
    */

    // Build breadcrumbs
    const menu = getMenuFromPath(user,menuConfig,path);  

    if(menu === null || !Object.hasOwn(menuConfig,menu)){
        return [];
    }

    let config = menuConfig[menu];

    let breadcrumbs = [];
    while(config?.parent){
        breadcrumbs.push({ 
            icon: config?.icon,
            title: config?.title, 
            path: `/${config?.path}` 
        });
        config = menuConfig[config?.parent];
    }

    breadcrumbs.push({ 
        icon: config?.icon,
        title: config?.title, 
        path: `/${config?.path}` 
    });
    
    return breadcrumbs.reverse();
}

// render breadcrumb
export const renderBreadcrumb = (user,router,menuConfig) => {

    const showIcon = false;
    const breadcrumb = getBreadcrumb(user,router,menuConfig);

    return (
        breadcrumb.length > 0 ?
            <div
                className='items-center flex'
            >
                {
                    breadcrumb.map((b, index) => (
                        index !== breadcrumb.length - 1 ?
                            <div 
                                key={b.path} 
                                onClick={() => router?.navigate(b.path)}
                                className="cursor-pointer flex items-center"
                            >
                                {showIcon && b?.icon} {b?.title}<span className="mx-1">/</span> 
                            </div>
                        :
                            <div 
                                key={b.path} 
                                className='font-bold flex items-center'
                            >
                                {showIcon && b?.icon} {b?.title}
                            </div>
                    ))
                }
            </div> 
        :
            null
    );
}

// the breadcrumb if we want to show it on the page content (default behaviour)
export const getMenuBreadcrumbsPageContent = (t,router) => {
    
    // return null to get default title (taken from the menu config)
    var breadcrumbs = null;

    var path = router.pathname;

    
    // if item/view/N do not show breadcrumb
    if(checkViewPath(path)){
        breadcrumbs = [];     
    }
    else{
        // if submenu, show breadcrumbs, if not, do not show
        var split = path.split('/');
        if(split?.length > 2){

            // if not menus with tabs
            const parent = split[1];
            switch(parent){

                case 'notifications':
                case 'tickets':
                case 'resourcesrentals':
                    // no breadcrumb
                    breadcrumbs = [];
                    break;

                case 'configuration':
                    // configuration/tickets also has no breadcrumb
                    breadcrumbs = [];
                    break;

                    default:
                        // default option, show breadcrumb
                        breadcrumbs = null;
                        break;
            }
        }
        else{
            // no breadcrumb
            breadcrumbs = [];
        }
    }

    return breadcrumbs;
}

// title for the page
export const getPageTitle = (t,router,table,menuConfig) => {
    
    var title = '';

    let config = menuConfig;
    if(!config && t){
        try {
            config = getMenu(t)[1];
        }
        catch(e){}
    }

    if(router?.pathname){
        let path = router.pathname.replace(/^\//, '');
        if(config && Object?.hasOwn(config,path)){
            title = config[path]?.title;
        }

    }

    if(!title && table){
        if(config){
            if(Object?.hasOwn(config,table)){
                title = config[table]?.title;
            }
            else{
                const matchingItem = Object.values(config).find(item => item.segment === table || item.path === table);
                if(matchingItem?.title){
                    title = matchingItem.title;
                }
            }
        }

        if(!title){
            if(t){
                const titleKey = `${table}.title`;
                if(t(titleKey) !== titleKey){
                    title = t(titleKey);
                }
                else {
                    if(t(table) !== table){
                        title = t(table);
                    }
                    else {
                        title = table.charAt(0).toUpperCase() + table.slice(1).replace(/_/g, ' ');
                    }
                }
            }
        }
    }

    return title;
}